import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
// date-fns used in original tooltip, kept for potential future use
import {
    ResponsiveContainer,
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Cell,
    ReferenceLine,
} from 'recharts';

export const SpendVsROASScatterChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .reduce<Array<{ date: string; adSpend: number; roas: number }>>((acc, [date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const adSpend = metrics?.adSpend ?? 0;
                const revenue = metrics?.revenue ?? 0;

                if (adSpend > 0) {
                    acc.push({
                        date,
                        adSpend: Math.round(adSpend * 100) / 100,
                        roas: Math.round((revenue / adSpend) * 100) / 100,
                    });
                }
                return acc;
            }, []);
    }, [finance?.dailyBreakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ScatterChart margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="adSpend"
                            type="number"
                            name={t('charts.adSpend', 'Ad Spend')}
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            label={{
                                value: t('charts.adSpend', 'Ad Spend'),
                                position: 'insideBottom',
                                offset: -5,
                                style: CHART_STYLES.axis.tick,
                            }}
                        />
                        <YAxis
                            dataKey="roas"
                            type="number"
                            name="ROAS"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(v: number) => `${v}x`}
                            label={{
                                value: 'ROAS',
                                angle: -90,
                                position: 'insideLeft',
                                style: CHART_STYLES.axis.tick,
                            }}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={{ strokeDasharray: '3 3' }}
                            formatter={(value: number, name: string) => {
                                if (name === t('charts.adSpend', 'Ad Spend')) {
                                    return [formatCompactNumber(value, currencySymbol), name];
                                }
                                return [`${value}x`, name];
                            }}
                            labelFormatter={(_v: any) => ''}
                        />
                        <ReferenceLine
                            y={1}
                            stroke={CHART_COLORS.gray[400]}
                            strokeDasharray="4 4"
                            label={{
                                value: '1x ROAS',
                                position: 'right',
                                style: { fill: CHART_COLORS.gray[400], fontSize: 10 },
                            }}
                        />
                        <Scatter
                            data={chartData}
                            name={t('charts.dailyPerformance', 'Daily Performance')}
                        >
                            {chartData.map((entry, index) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill={entry.roas >= 1 ? CHART_COLORS.revenue : CHART_COLORS.expense}
                                />
                            ))}
                        </Scatter>
                    </ScatterChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
